import { API_URL } from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Dashboard.css';

function Dashboard({ onLogout }) {
  const [user, setUser] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [files, setFiles] = useState([]);
  const [caseName, setCaseName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  // All 27 modules with checkboxes
  const [modules, setModules] = useState({
    reversals: true,
    transfers: true,
    atm: true,
    fees: true,
    top_payees: true,
    top_income: true,
    by_type: true,
    daily_timeline: true,
    weekend: true,
    round_numbers: true,
    benefit_consistency: true,
    geographic: true,
    third_party: true,
    shell_company: true,
    velocity: true,
    unreported_income: true,
    misuse: true,
    asset_concealment: true,
    lifestyle: true,
    checklist: true,
  });

  useEffect(() => {
    fetchUserData();
    fetchAnalyses();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_URL + '/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
    }
  };

  const fetchAnalyses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_URL + '/analyses', {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("API Response data:", response.data);
      if (response.data && response.data[0]) {
        console.log("First analysis object:", response.data[0]);
        console.log("textract_confidence value:", response.data[0].textract_confidence);
      }
      setAnalyses(response.data);
    } catch (err) {
      console.error('Failed to fetch analyses:', err);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => (file.type === 'application/pdf' || file.type.startsWith('image/'))
    );
    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(
      file => (file.type === 'application/pdf' || file.type.startsWith('image/'))
    );
    if (selectedFiles.length > 0) {
      setFiles(prev => [...prev, ...selectedFiles]);
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const toggleModule = (key) => {
    setModules(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAll = () => {
    const allTrue = {};
    Object.keys(modules).forEach(key => { allTrue[key] = true; });
    setModules(allTrue);
  };

  const deselectAll = () => {
    const allFalse = {};
    Object.keys(modules).forEach(key => { allFalse[key] = false; });
    setModules(allFalse);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one PDF file');
      return;
    }

    setError('');
    setSuccess('');
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('statement_type', 'bank');
    formData.append('case_name', caseName || files[0].name.replace('.pdf', ''));
    
    const selected = Object.entries(modules).filter(([_, v]) => v).map(([k, _]) => k).join(',');
    formData.append('modules', selected || 'all');

    try {
      const token = localStorage.getItem('token');
      await axios.post(API_URL + '/analyze/bank-statement', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      setSuccess('Analysis complete! Download your report below.');
      setFiles([]);
      setCaseName('');
      fetchAnalyses();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (analysisId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/download/${analysisId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analysis_${analysisId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Download failed.');
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-header-container">
          <div className="logo-text">VERALON</div>
          <div className="dashboard-header-right">
            <span className="user-name">{user?.email}</span>
            <button onClick={handleLogout} className="btn-logout">Logout</button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-container">
          <div className="dashboard-welcome">
            <h1>Welcome back{user?.full_name ? `, ${user.full_name}` : ''}!</h1>
            <p>Upload bank statements for analysis</p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="upload-section-grid">
            {/* DOCUMENTS */}
            <div className="upload-card-new">
              <h3>📁 DOCUMENTS</h3>
              <div
                className={`dropzone ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="dropzone-icon">⬇</div>
                <p>Drag & Drop PDFs or Images Here</p>
                <p className="or-text">or</p>
                <label className="btn-browse">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,image/*" accept=".pdf" multiple onChange={handleFileInput} style={{ display: 'none' }} />
                  Browse for Files
                </label>
              </div>

              {files.length > 0 && (
                <div className="files-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <span>📄 {file.name}</span>
                      <button onClick={() => removeFile(index)} className="btn-remove-file">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ANALYSIS MODULES */}
            <div className="modules-card-new">
              <h3>📊 ANALYSIS MODULES</h3>
              <div style={{marginBottom: '1rem', display: 'flex', gap: '0.5rem'}}>
                <button onClick={selectAll} style={{padding: '0.5rem 1rem', fontSize: '12px', border: '1px solid #cbd5e0', borderRadius: '4px', background: 'white', cursor: 'pointer'}}>Select All</button>
                <button onClick={deselectAll} style={{padding: '0.5rem 1rem', fontSize: '12px', border: '1px solid #cbd5e0', borderRadius: '4px', background: 'white', cursor: 'pointer'}}>Clear All</button>
              </div>
              <div className="module-scroll-container">
                <div className="module-section">
                  <div className="module-section-title">STANDARD ANALYSIS</div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>Reversals & Chargebacks</label>
                  </div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>Transfers, Wires & ACH</label>
                  </div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>ATM & Cash Withdrawals</label>
                  </div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>Fees & Charges</label>
                  </div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>Top Payees by Outflow</label>
                  </div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>Top Income Sources</label>
                  </div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>Breakdown by Transaction Type</label>
                  </div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>Daily Activity Timeline</label>
                  </div>
                  <div className="module-checkbox-item disabled">
                    <input type="checkbox" checked disabled />
                    <label>Weekend / Off-Hours Activity</label>
                  </div>
                </div>

                <div className="module-section">
                  <div className="module-section-title">INVESTIGATIVE MODULES</div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.round_numbers} onChange={() => toggleModule('round_numbers')} />
                    <label onClick={() => toggleModule('round_numbers')}>Round Number Transactions</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.benefit_consistency} onChange={() => toggleModule('benefit_consistency')} />
                    <label onClick={() => toggleModule('benefit_consistency')}>Benefit Payment Consistency</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.geographic} onChange={() => toggleModule('geographic')} />
                    <label onClick={() => toggleModule('geographic')}>Geographic Anomaly Analysis</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.third_party} onChange={() => toggleModule('third_party')} />
                    <label onClick={() => toggleModule('third_party')}>Third-Party Recipient Network</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.shell_company} onChange={() => toggleModule('shell_company')} />
                    <label onClick={() => toggleModule('shell_company')}>Shell Company Indicators</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.velocity} onChange={() => toggleModule('velocity')} />
                    <label onClick={() => toggleModule('velocity')}>Velocity — Rapid Outflow</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.unreported_income} onChange={() => toggleModule('unreported_income')} />
                    <label onClick={() => toggleModule('unreported_income')}>Unreported Income Detection</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.misuse} onChange={() => toggleModule('misuse')} />
                    <label onClick={() => toggleModule('misuse')}>Financial Misuse Red Flags</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.asset_concealment} onChange={() => toggleModule('asset_concealment')} />
                    <label onClick={() => toggleModule('asset_concealment')}>Asset / Investment Concealment</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.lifestyle} onChange={() => toggleModule('lifestyle')} />
                    <label onClick={() => toggleModule('lifestyle')}>Lifestyle vs. Income Analysis</label>
                  </div>
                  <div className="module-checkbox-item">
                    <input type="checkbox" checked={modules.checklist} onChange={() => toggleModule('checklist')} />
                    <label onClick={() => toggleModule('checklist')}>⚡ Investigative Action Checklist</label>
                  </div>
                </div>
              </div>
            </div>

            {/* REPORT SETTINGS */}
            <div className="settings-card-new">
              <h3>⚙️ REPORT SETTINGS</h3>
              <div className="form-group">
                <label>Case / Subject Name:</label>
                <input
                  type="text"
                  value={caseName}
                  onChange={(e) => setCaseName(e.target.value)}
                  placeholder="Optional"
                  className="input-text"
                />
              </div>

              {uploading ? (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p>{uploadProgress}% uploaded... Analyzing...</p>
                </div>
              ) : (
                <button onClick={handleUpload} className="btn-run-analysis" disabled={files.length === 0}>
                  ⚡ RUN ANALYSIS
                </button>
              )}
            </div>
          </div>

          {/* RECENT ANALYSES */}
          <div className="analyses-section">
            <h2>Recent Analyses</h2>
            {analyses.length === 0 ? (
              <div className="empty-state">
                <p>No analyses yet. Upload your first bank statement!</p>
              </div>
            ) : (
              <div className="analyses-list">
                {analyses.map((analysis) => (
                  <div key={analysis.id} className="analysis-card">
                    <div className="analysis-info">
                      <h3>{analysis.filename}</h3>
                      <p className="analysis-date">{formatDate(analysis.created_at)}</p>
                      <div className="analysis-meta">
                        <span className={`status-badge status-${analysis.status}`}>{analysis.status}</span>
                        {analysis.transactions_count && (
                          <span className="transaction-count">{analysis.transactions_count} transactions</span>
                        )}
                        {analysis.textract_confidence !== null && (
                          <span className="confidence-score">Confidence: {analysis.textract_confidence.toFixed(1)}%</span>
                        )}
                      </div>
                    </div>
                    {analysis.status === 'completed' && (
                      <button onClick={() => handleDownload(analysis.id)} className="btn-download">
                        Download Report
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
